import type {
  Source,
  SourceParseStatus,
} from '@/common/db-typebox/source.typebox';
import { useTreatyClient } from '@/lib/client';
import { validateFile } from '@/lib/upload';
import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Modal,
  Progress,
  Spin,
  Typography,
  message,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload';
import Upload from 'antd/es/upload';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Calculate progress percentage based on parse_status
const getProgressPercentage = (status: SourceParseStatus): number => {
  switch (status) {
    case 'pending':
      return 0;
    case 'processing':
      return 50;
    case 'completed':
      return 100;
    case 'failed':
      return 100;
    case 'cancelled':
      return 100;
    default:
      return 0;
  }
};

// Get status text in Chinese
const getStatusText = (status: SourceParseStatus): string => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'Processing';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown status';
  }
};

// Get status color
const getStatusColor = (status: SourceParseStatus): string => {
  switch (status) {
    case 'pending':
      return '#faad14';
    case 'processing':
      return '#1890ff';
    case 'completed':
      return '#52c41a';
    case 'failed':
      return '#f5222d';
    case 'cancelled':
      return '#d9d9d9';
    default:
      return '#d9d9d9';
  }
};

interface UploadWineModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (data: {
    id: string;
    name: string;
    url: string;
    parse_status: string;
  }) => void;
}

const { Text } = Typography;

const UploadWineModal: FC<UploadWineModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [latestSource, setLatestSource] = useState<Source | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileRef = useRef<File | null>(null);
  const client = useTreatyClient();

  const fetchLatestSource = useCallback(async () => {
    setLoading(true);
    try {
      const response = await client.api.wine_product.upload.latest.get();
      if (response.data?.success) {
        setLatestSource(response.data.source);
      } else {
        setLatestSource(null);
      }
    } catch (error) {
      setLatestSource(null);
      console.error('Failed to fetch latest upload status:', error);
    } finally {
      setLoading(false);
    }
  }, [client]);

  // Setup polling for latest source status
  useEffect(() => {
    if (visible) {
      fetchLatestSource();

      // Start polling when modal is visible
      timerRef.current = setInterval(fetchLatestSource, 10000);
    }

    return () => {
      // Clear interval when component unmounts or modal closes
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [visible, fetchLatestSource]);

  // Check if upload is allowed based on latestSource status
  const isUploadAllowed = useMemo(() => {
    if (!latestSource) return true;
    return ['completed', 'failed', 'cancelled'].includes(
      latestSource.parse_status,
    );
  }, [latestSource]);

  // Get reason why upload is not allowed
  const getUploadDisabledReason = useMemo(() => {
    if (!latestSource) return '';
    if (['pending', 'processing'].includes(latestSource.parse_status)) {
      return `Cannot upload a new file while another file is ${latestSource.parse_status.toLowerCase()}`;
    }
    return '';
  }, [latestSource]);

  const handleUpload = async () => {
    if (fileList.length === 0 || !fileRef.current) {
      message.warning('Please select a file to upload');
      return;
    }

    // Double-check if upload is allowed
    if (!isUploadAllowed) {
      message.error(getUploadDisabledReason);
      return;
    }

    // Update file status to uploading
    setFileList((prevList) =>
      prevList.map((file) => ({
        ...file,
        status: 'uploading',
      })),
    );
    setUploading(true);

    try {
      const response = await client.api.wine_product.upload.post({
        file: fileRef.current,
      });

      if (response.data?.success) {
        // Update file status to complete
        setFileList((prevList) =>
          prevList.map((file) => ({
            ...file,
            status: 'done',
            response: response.data,
          })),
        );
        message.success('File uploaded successfully');
        onSuccess(response.data.source);

        fetchLatestSource();
      } else {
        // Update file status to error
        setFileList((prevList) =>
          prevList.map((file) => ({
            ...file,
            status: 'error',
            error: new Error(response.error?.value?.message || 'Unknown error'),
          })),
        );
        message.error(
          `Upload failed: ${response.error?.value?.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      // Update file status to error
      setFileList((prevList) =>
        prevList.map((file) => ({
          ...file,
          status: 'error',
          error: error instanceof Error ? error : new Error('Unknown error'),
        })),
      );
      message.error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    fileRef.current = null;
    onCancel();
  };

  // handle retry
  const handleRetry = async () => {
    if (!latestSource || latestSource.parse_status !== 'failed') return;

    setRetrying(true);
    try {
      const response = await client.api.wine_product
        .upload({
          id: latestSource.id,
        })
        .retry.post({});

      if (response.data?.success) {
        message.success('Successfully retried');
        // Refresh latest status
        fetchLatestSource();
      } else {
        message.error(
          `Retry failed: ${response.error?.value?.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      message.error(
        `Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setRetrying(false);
    }
  };

  // Handle file status changes
  const handleChange: UploadProps['onChange'] = (info) => {
    // When using beforeUpload to intercept uploads, status won't update automatically
    // This function only triggers when files are added or removed
    if (info.file.status === 'removed') {
      setFileList([]);
      fileRef.current = null;
    }
  };

  return (
    <Modal
      title="Upload Wine List"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {latestSource?.parse_status === 'completed' ? 'Confirm' : 'Close'}
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0 || uploading || !isUploadAllowed}
          loading={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Wine List'}
        </Button>,
      ]}
    >
      {!isUploadAllowed && (
        <Alert
          message={getUploadDisabledReason}
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      {latestSource && latestSource.parse_status === 'failed' && (
        <Alert
          message="Upload processing failed"
          description={
            <div>
              <p>
                The system encountered an error while processing your file.
                {latestSource.error
                  ? `Error information: ${latestSource.error}`
                  : ''}
              </p>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                size="small"
                loading={retrying}
                onClick={handleRetry}
                className="mt-2"
              >
                Retry processing
              </Button>
            </div>
          }
          type="error"
          showIcon
          className="mb-4"
        />
      )}

      <Upload.Dragger
        accept=".pdf,.jpg,.jpeg,.png"
        maxCount={1}
        fileList={fileList}
        disabled={uploading || !isUploadAllowed}
        onChange={handleChange}
        beforeUpload={(file) => {
          if (!isUploadAllowed) {
            message.warning(getUploadDisabledReason);
            return false;
          }

          if (!validateFile(file)) {
            return false;
          }

          // Save reference to original file object
          fileRef.current = file;

          // Set initial file state when adding a file
          setFileList([
            {
              ...file,
              uid: file.uid || Date.now().toString(),
              name: file.name,
            },
          ]);
          return false; // Prevent automatic upload
        }}
        onRemove={() => {
          setFileList([]);
          fileRef.current = null;
        }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined className="text-2xl" />
        </p>
        <p className="ant-upload-text">
          Drag and drop the wine list file here, or click to browse
        </p>
        <p className="ant-upload-hint text-xs mt-1">
          Only PDF, JPG, or PNG files are supported. Images must be under 15MB,
          PDFs under 100MB.
        </p>
      </Upload.Dragger>

      {(latestSource || loading) && (
        <div className="mt-6 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <Text strong>Latest upload status</Text>
            <div>
              {latestSource && latestSource.parse_status === 'failed' && (
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={retrying}
                  onClick={handleRetry}
                  className="mr-2"
                >
                  Retry
                </Button>
              )}
              <Button
                type="link"
                size="small"
                onClick={fetchLatestSource}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-4">
              <Spin tip="Loading..." />
            </div>
          ) : latestSource ? (
            <div className="bg-gray-50 p-3 rounded">
              <div className="mb-2 flex justify-between">
                <Text>{latestSource.name}</Text>
                <Text
                  style={{ color: getStatusColor(latestSource.parse_status) }}
                  strong
                >
                  {getStatusText(latestSource.parse_status)}
                </Text>
              </div>
              <Progress
                percent={getProgressPercentage(latestSource.parse_status)}
                status={
                  latestSource.parse_status === 'failed'
                    ? 'exception'
                    : latestSource.parse_status === 'cancelled'
                      ? 'exception'
                      : undefined
                }
                showInfo={latestSource.parse_status !== 'cancelled'}
                strokeColor={getStatusColor(latestSource.parse_status)}
              />
              <Text type="secondary" className="text-xs block mt-1">
                Upload time:{' '}
                {new Date(latestSource.created_at).toLocaleString()}
              </Text>
              {latestSource.parse_status === 'failed' && latestSource.error && (
                <Text type="danger" className="text-xs block mt-1">
                  Error information: {latestSource.error}
                </Text>
              )}
            </div>
          ) : (
            <Text type="secondary">No upload record</Text>
          )}
        </div>
      )}
    </Modal>
  );
};

export default UploadWineModal;
